import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Search, Eye, RefreshCw, Mail, Loader2, CheckCircle2, XCircle,
  X, ChevronLeft, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmailCenterTab({ adminToken }) {
  const getHeaders = () => ({ Authorization: `Bearer ${adminToken}` });
  const [leads, setLeads] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [composeModal, setComposeModal] = useState(null);
  const [composeSubject, setComposeSubject] = useState("");
  const [composeBody, setComposeBody] = useState("");
  const [composeSending, setComposeSending] = useState(false);

  const fetchLeads = useCallback(async () => {
    try {
      const params = { page, limit: 20 };
      if (searchTerm) params.search = searchTerm;
      const res = await axios.get(`${API}/admin/emails`, { params, headers: { Authorization: `Bearer ${adminToken}` } });
      setLeads(res.data.leads || []);
      setPagination({ total: res.data.total || 0, pages: res.data.pages || 1 });
    } catch (err) { if (err.response?.status === 401) window.location.reload(); }
  }, [page, searchTerm, adminToken]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const handleViewHistory = async (email) => {
    if (history?.email === email) { setHistory(null); return; }
    setHistoryLoading(true);
    try { const res = await axios.get(`${API}/admin/emails/${encodeURIComponent(email)}/history`, { headers: getHeaders() }); setHistory(res.data); }
    catch { toast.error("Failed to load email history"); }
    finally { setHistoryLoading(false); }
  };

  const handleSendEmail = async () => {
    if (!composeModal || !composeSubject.trim() || !composeBody.trim()) { toast.error("Subject and body are required"); return; }
    setComposeSending(true);
    try {
      await axios.post(`${API}/admin/emails/send`, { to: composeModal, subject: composeSubject, body: composeBody }, { headers: getHeaders() });
      toast.success(`Email sent to ${composeModal}`);
      setComposeModal(null); setComposeSubject(""); setComposeBody("");
    } catch (err) { toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Failed to send email"); }
    finally { setComposeSending(false); }
  };

  return (
    <div className="space-y-4">
      {/* Compose Modal */}
      {composeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setComposeModal(null)}>
          <Card className="w-full max-w-lg bg-white" onClick={e => e.stopPropagation()}>
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle className="text-base text-[#111827]">Send Email to {composeModal}</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setComposeModal(null)}><X className="w-4 h-4" /></Button>
            </CardHeader>
            <CardContent className="space-y-3">
              <div><Label className="text-sm text-slate-700">To</Label><Input value={composeModal} disabled className="bg-slate-50 border-slate-300" /></div>
              <div><Label className="text-sm text-slate-700">Subject</Label><Input value={composeSubject} onChange={e => setComposeSubject(e.target.value)} placeholder="Subject line..." className="bg-white border-slate-300" data-testid="compose-subject" /></div>
              <div><Label className="text-sm text-slate-700">Body (HTML supported)</Label>
                <textarea value={composeBody} onChange={e => setComposeBody(e.target.value)} placeholder="Type your message..." rows={6} className="w-full rounded-md border border-slate-300 p-3 text-sm bg-white" data-testid="compose-body" /></div>
              <Button onClick={handleSendEmail} disabled={composeSending} className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="compose-send-btn">
                {composeSending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}Send Email
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search */}
      <Card className="bg-white border-[#E5E7EB]">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
              <Input placeholder="Search by email or business name..." value={searchTerm} onChange={e => { setSearchTerm(e.target.value); setPage(1); }} className="pl-9 bg-white border-[#E5E7EB]" data-testid="email-search" />
            </div>
            <Button variant="outline" size="sm" onClick={fetchLeads}><RefreshCw className="w-4 h-4" /></Button>
          </div>
        </CardContent>
      </Card>

      {/* Leads Table */}
      <Card className="bg-white border-[#E5E7EB]">
        <CardHeader><CardTitle className="text-lg text-[#111827]">Customer Emails ({pagination.total})</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="border-[#E5E7EB] bg-[#F9FAFB]">
                <TableHead className="text-[#6B7280]">Email</TableHead>
                <TableHead className="text-[#6B7280]">Business</TableHead>
                <TableHead className="text-center text-[#6B7280]">Email 1</TableHead>
                <TableHead className="text-center text-[#6B7280]">Email 2</TableHead>
                <TableHead className="text-center text-[#6B7280]">Email 3</TableHead>
                <TableHead className="text-center text-[#6B7280]">Paid</TableHead>
                <TableHead className="text-right text-[#6B7280]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {leads.length > 0 ? leads.map((lead, i) => (
                <TableRow key={lead.email + "-" + i} className="border-[#E5E7EB]">
                  <TableCell className="text-sm text-[#111827] font-medium">{lead.email}</TableCell>
                  <TableCell className="text-sm text-[#6B7280]">{lead.business_name || "—"}</TableCell>
                  <TableCell className="text-center">{lead.email1_sent ? <CheckCircle2 className="w-4 h-4 text-emerald-500 mx-auto" /> : <XCircle className="w-4 h-4 text-slate-300 mx-auto" />}</TableCell>
                  <TableCell className="text-center">{lead.email2_sent ? <CheckCircle2 className="w-4 h-4 text-emerald-500 mx-auto" /> : <XCircle className="w-4 h-4 text-slate-300 mx-auto" />}</TableCell>
                  <TableCell className="text-center">{lead.email3_sent ? <CheckCircle2 className="w-4 h-4 text-emerald-500 mx-auto" /> : <XCircle className="w-4 h-4 text-slate-300 mx-auto" />}</TableCell>
                  <TableCell className="text-center">{lead.paid ? <Badge className="bg-emerald-50 text-emerald-600">Paid</Badge> : <Badge className="bg-slate-50 text-slate-500">Free</Badge>}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => handleViewHistory(lead.email)} className="text-xs text-blue-500" data-testid={`email-history-${i}`}>
                        <Eye className="w-3.5 h-3.5 mr-1" />History
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => { setComposeModal(lead.email); setComposeSubject(""); setComposeBody(""); }} className="text-xs text-indigo-500" data-testid={`email-compose-${i}`}>
                        <Mail className="w-3.5 h-3.5 mr-1" />Send
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )) : <TableRow><TableCell colSpan={7} className="text-center py-8 text-[#9CA3AF]">No leads found</TableCell></TableRow>}
            </TableBody>
          </Table>

          {pagination.pages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#E5E7EB]">
              <span className="text-sm text-[#6B7280]">Page {page} of {pagination.pages}</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}><ChevronLeft className="w-4 h-4" /></Button>
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(pagination.pages, p + 1))} disabled={page >= pagination.pages}><ChevronRight className="w-4 h-4" /></Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Email History Panel */}
      {historyLoading && <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>}
      {history && !historyLoading && (
        <Card className="bg-white border-[#E5E7EB]">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base text-[#111827]">Email History: {history.email}</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setHistory(null)}><X className="w-4 h-4" /></Button>
          </CardHeader>
          <CardContent>
            {history.leads?.length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-medium text-[#374151] mb-2">Drip Email Status</p>
                {history.leads.map((lead, i) => (
                  <div key={i} className="p-3 bg-slate-50 rounded-lg mb-2 text-sm">
                    <p className="font-medium text-[#111827]">{lead.business_name || "Unknown Business"}</p>
                    <div className="flex flex-wrap gap-3 mt-1">
                      <span className={lead.email1_sent ? "text-emerald-600" : "text-slate-400"}>Email 1: {lead.email1_sent ? `Sent ${lead.email1_sent_at ? new Date(lead.email1_sent_at).toLocaleDateString() : ""}` : "Pending"}</span>
                      <span className={lead.email2_sent ? "text-emerald-600" : "text-slate-400"}>Email 2: {lead.email2_sent ? `Sent ${lead.email2_sent_at ? new Date(lead.email2_sent_at).toLocaleDateString() : ""}` : "Pending"}</span>
                      <span className={lead.email3_sent ? "text-emerald-600" : "text-slate-400"}>Email 3: {lead.email3_sent ? `Sent ${lead.email3_sent_at ? new Date(lead.email3_sent_at).toLocaleDateString() : ""}` : "Pending"}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {history.sent_emails?.length > 0 ? (
              <div>
                <p className="text-sm font-medium text-[#374151] mb-2">Manual Emails Sent</p>
                {history.sent_emails.map((se, i) => (
                  <div key={i} className="p-3 border border-[#E5E7EB] rounded-lg mb-2">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-[#111827]">{se.subject}</p>
                      <span className="text-xs text-[#9CA3AF]">{se.sent_at ? new Date(se.sent_at).toLocaleString() : "—"}</span>
                    </div>
                    <p className="text-xs text-[#6B7280] mt-1">{se.body_preview}</p>
                    <p className="text-xs text-[#9CA3AF] mt-1">Sent by: {se.sent_by || "admin"}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[#9CA3AF] text-center py-4">No manual emails sent yet</p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
